/**
 * Open a Stripe hosted-checkout URL from anywhere in the app.
 *
 * Bug fix (2026-09-15): `window.location.href = url` navigates the CURRENT window,
 * which is fine on a deployed site but fails when the app is loaded inside an
 * iframe (e.g. Emergent's preview pane). Stripe checkout can't render inside a
 * cross-origin iframe reliably — you get a blank pane where the checkout should be.
 *
 * This helper does three things in order of preference:
 *   1. If we're inside an iframe, open Stripe in a NEW top-level tab so the
 *      buyer sees the real checkout page.
 *   2. Otherwise, navigate the current window to Stripe as before.
 *   3. If the popup blocker eats the new window, fall back to `window.top.location`.
 */
export function openStripeCheckout(url) {
    if (!url) return;

    // Detect iframe. `window.top !== window.self` is true when we're framed;
    // accessing `window.top` across origins can throw in some sandboxes, hence try/catch.
    let framed = false;
    try {
        framed = window.top !== window.self;
    } catch {
        framed = true;
    }

    if (framed) {
        // Open Stripe in a brand-new top-level tab. Some browsers block this if
        // it's not called from a direct user gesture — every caller here IS a
        // click handler, so we're fine. If a popup blocker still refuses, we
        // fall back to `top.location`.
        const win = window.open(url, "_blank", "noopener,noreferrer");
        if (!win) {
            try {
                window.top.location.href = url;
            } catch {
                // Last-resort: at least don't leave the user with a dead click.
                window.location.href = url;
            }
        }
        return;
    }

    window.location.href = url;
}
